/**
 * Class-Transformer Shim for Browser Environments
 *
 * This file provides no-op implementations of class-transformer decorators and functions
 * for use in web applications. When bundling for the browser, Vite can be configured to
 * alias 'class-transformer' to this file, preventing the transformation library from
 * being bundled into the browser application.
 *
 * The decorators do nothing in the browser - they just return no-op decorators.
 * Transformation only happens on the server side.
 */

function noop(): PropertyDecorator {
	return () => {
		// No-op
	};
}

// Decorator functions
export function Transform(_transformFn: unknown, _options?: unknown): PropertyDecorator {
	return noop();
}

export function Type(_typeFunction?: unknown, _options?: unknown): PropertyDecorator {
	return noop();
}

export function Expose(_options?: unknown): PropertyDecorator {
	return noop();
}

export function Exclude(_options?: unknown): PropertyDecorator {
	return noop();
}

export function TransformInstanceToPlain(_options?: unknown): MethodDecorator {
	return () => {
		// No-op
	};
}

export function TransformPlainToInstance(
	_classType: unknown,
	_options?: unknown,
): MethodDecorator {
	return () => {
		// No-op
	};
}

export function TransformInstanceToInstance(_options?: unknown): MethodDecorator {
	return () => {
		// No-op
	};
}

// Transformation functions - return input unchanged in browser
export function plainToInstance<T>(
	_cls: ClassConstructor<T>,
	plain: unknown,
	_options?: unknown,
): T {
	return plain as T;
}

export function plainToClass<T>(
	_cls: ClassConstructor<T>,
	plain: unknown,
	_options?: unknown,
): T {
	return plain as T;
}

export function instanceToPlain<T>(
	object: T,
	_options?: unknown,
): Record<string, unknown> {
	return object as unknown as Record<string, unknown>;
}

export function classToPlain<T>(
	object: T,
	_options?: unknown,
): Record<string, unknown> {
	return object as unknown as Record<string, unknown>;
}

export function instanceToInstance<T>(object: T, _options?: unknown): T {
	return object;
}

export function classToClass<T>(object: T, _options?: unknown): T {
	return object;
}

export function serialize<T>(object: T, _options?: unknown): string {
	return JSON.stringify(object);
}

export function deserialize<T>(
	_cls: ClassConstructor<T>,
	json: string,
	_options?: unknown,
): T {
	return JSON.parse(json) as T;
}

export function deserializeArray<T>(
	_cls: ClassConstructor<T>,
	json: string,
	_options?: unknown,
): Array<T> {
	return JSON.parse(json) as Array<T>;
}

// Types
export type ClassConstructor<T> = {
	new (...args: Array<unknown>): T;
};

export interface TransformOptions {
	groups?: Array<string>;
	version?: number;
	excludeExtraneousValues?: boolean;
	enableImplicitConversion?: boolean;
	exposeDefaultValues?: boolean;
	exposeUnsetFields?: boolean;
	enableCircularCheck?: boolean;
	strategy?: 'excludeAll' | 'exposeAll';
}

export interface TransformFnParams {
	value: unknown;
	key: string;
	obj: unknown;
	type: TransformationType;
	options: TransformOptions;
}

export enum TransformationType {
	PLAIN_TO_CLASS = 0,
	CLASS_TO_PLAIN = 1,
	CLASS_TO_CLASS = 2,
}